/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
// antd:
// import { Divider } from "antd";
// import { LoadingOutlined } from "@ant-design/icons";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getUser, getAppState, changeAppState, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

const RZicon:FC<TuniversalElementProps> = ({attributes}) => {
  RZicon.displayName = "Icon";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, align} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  const {color, iconColor, bgColor, size, iconSize, borders, iconClass} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!~userClss.indexOf("rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    if (align) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      (align || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt:string) => {
        if ((/^(left|center|right|between|around|stretch|top|middle|bottom|row|column|wrap|nowrap|first|last|none|auto|\d+)/igm).test(tt) && !~userClss.indexOf("uk-flex-" + tt)) {
          userClss.push("uk-flex-" + tt);
        }
      });
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions.disabled, borders, align]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || iconColor || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr; est.borderTopColor = clr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, iconColor, theme, permissions]);

  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng, rlng]);

  const rico = useMemo(() => {
    const ico:React.ReactElement | undefined = (
      <AppIcons key={key + "-icon"} icopath={elState.value || elState.attributes?.icon || elState.attributes?.path || ""}
        attrs={{className: iconClass || "rz-margin-right-5", color: color || iconColor, bgcolor: bgColor, size: iconSize || size}}
      />
    );
    return ico;
  }, [key, elState.value, elState.attributes?.icon, elState.attributes?.path, iconClass, color, iconColor, bgColor, iconSize, size]);

  const onClick = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    // Icon рендерится как span: CSS-класс uk-disabled не блокирует события, поэтому останавливаем их вручную.
    if (elState?.disabled || permissions.disabled === true) { return }
    if (fnc.config?.isDev) console.log({action: "<Icon key='" + key + "'> -> onClick()"});
    if (rootNamespace && (attributes.onClick || attributes.onclick) && rootNamespace[attributes.onClick || attributes.onclick]) {
      const clientFNC = rootNamespace[attributes.onClick || attributes.onclick];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
      }
    }
  }, [elState?.disabled, permissions.disabled, key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, navigate, safe]);

  const onmouseenter = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    if (elState?.disabled || permissions.disabled === true) { return }
    if (fnc.config?.isDev) console.log({action: "<Icon key='" + key + "'> -> onMouseEnter()"});
    if (rootNamespace && (attributes.onMouseEnter || attributes.onmouseenter) && rootNamespace[attributes.onMouseEnter || attributes.onmouseenter]) {
      const clientFNC = rootNamespace[attributes.onMouseEnter || attributes.onmouseenter];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [elState?.disabled, permissions.disabled, key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, safe]);

  const onmouseleave = useCallback<React.MouseEventHandler<HTMLElement>>((event) => {
    if (elState?.disabled || permissions.disabled === true) { return }
    if (fnc.config?.isDev) console.log({action: "<Icon key='" + key + "'> -> onMouseLeave()"});
    if (rootNamespace && (attributes.onMouseLeave || attributes.onmouseleave) && rootNamespace[attributes.onMouseLeave || attributes.onmouseleave]) {
      const clientFNC = rootNamespace[attributes.onMouseLeave || attributes.onmouseleave];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [elState?.disabled, permissions.disabled, key, rootNamespace, attributes, rootAttributes.key, user, lng, dct, dispatch, safe]);

  return (
    <SpanE style={styl} className={clsu.join(" ")} uk-tooltip={tooltip} aria-disabled={elState?.disabled || permissions.disabled === true} onClick={onClick} onMouseEnter={onmouseenter} onMouseLeave={onmouseleave}>
      {rico}
    </SpanE>
  );
}

export default RZicon;
/* eslint-enable @typescript-eslint/no-unused-vars */
